import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Worker, Viewer, LoadError } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload } from "@fortawesome/free-solid-svg-icons";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import styles from "./DocsPage.module.scss";
import { SpecialZoomLevel } from "@react-pdf-viewer/core";

type LangType = "en" | "zh";

interface FileMapType {
  [key: string]: {
    [key in LangType]?: string;
  };
}

const DocsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();

  const getStepFromDocId = (id?: string): number => {
    if (!id) return 0;
    if (id === "note-foreign-assets") return 5;
    if (id === "q-and-a") return 6;
    const stepNum = parseInt(id.replace("step", ""));
    return isNaN(stepNum) ? 0 : stepNum;
  };

  const [currentStep, setCurrentStep] = useState(getStepFromDocId(docId));
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    setCurrentStep(getStepFromDocId(docId));
  }, [docId]);

  const fileMap: FileMapType = {
    step0: {
      en: "Step 0 - TY25 Filing Process.pdf",
      zh: "Step 0 - TY25新版报税流程.pdf",
    },
    step1: {
      en: "Step 1 - TY25 Engagement Letter.pdf",
    },
    step2: {
      en: "Step 2 - TY25 Tax Service Price Schedule.pdf",
      zh: "Step 2 - TY25报税服务价格表.pdf",
    },
    step3: {
      en: "Step 3 - Residency Status Self-Check Questionnaire.pdf",
      zh: "Step 3 - 报税身份自查问卷.pdf",
    },
    step4: {
      en: "Step 4 - Document Checklist.pdf",
      zh: "Step 4 - 材料清单.pdf",
    },
    step5: {
      en: "Note - Foreign Financial Assets Reporting Requirements.pdf",
      zh: "注 - 海外金融账户申报要求.pdf",
    },
    step6: {
      en: "Q & A.pdf",
    },
  };

  const getCurrentFile = (step: number) => {
    if (step === 1 || step === 6) return `/docs/${fileMap[`step${step}` as keyof FileMapType].en}`;
    const lang = i18n.language === "zh" ? ("zh" as const) : ("en" as const);
    const stepKey = `step${step}` as keyof FileMapType;
    return `/docs/${(fileMap[stepKey] as { [key in LangType]: string })[lang]}`;
  };

  const getStepTitle = (step: number) => {
    const fileName = getCurrentFile(step).split("/").pop()?.split(".pdf")[0];
    return (
      <div>
        <strong>Step {step}</strong>
        <div>{fileName}</div>
      </div>
    );
  };

  const handleStepChange = (stepNum: number) => {
    const docIds = [
      "step0",
      "step1",
      "step2",
      "step3",
      "step4",
      "note-foreign-assets",
      "q-and-a",
    ];
    navigate(`/docs/${docIds[stepNum]}`);
  };

  return (
    <div className={styles.docsContainer}>
      <h1>{t("docs.title")}</h1>
      <div className={styles.subtitle}>{t("docs.subtitle")}</div>
      <div className={styles.content}>
        <div className={styles.docSelector}>
          <select
            value={currentStep}
            onChange={(e) => handleStepChange(Number(e.target.value))}
            className={styles.selectBox}
          >
            <optgroup label={t("docs.steps")}>
              <option value={0}>Step 0 - {t("docs.filingProcess")}</option>
              <option value={1}>Step 1 - {t("docs.engagementLetter")}</option>
              <option value={2}>Step 2 - {t("docs.priceSchedule")}</option>
              <option value={3}>
                Step 3 - {t("docs.residencyQuestionnaire")}
              </option>
              <option value={4}>Step 4 - {t("docs.checklist")}</option>
            </optgroup>
            <optgroup label={t("docs.notes")}>
              <option value={5}>{t("docs.foreignAssets")}</option>
              <option value={6}>{t("docs.qAndA")}</option>
            </optgroup>
          </select>
        </div>

        <div className={styles.currentFileInfo}>
          <span className={styles.fileName}>
            {getCurrentFile(currentStep).split("/").pop()}
          </span>
          <a
            href={getCurrentFile(currentStep)}
            download
            className={styles.downloadButton}
          >
            <FontAwesomeIcon icon={faFileDownload} />
            {t("docs.download")}
          </a>
        </div>

        <div className={styles.pdfViewer}>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer
              fileUrl={getCurrentFile(currentStep)}
              plugins={[defaultLayoutPluginInstance]}
              defaultScale={
                window.innerWidth <= 768
                  ? Math.min(0.8, (window.innerWidth - 40) / 800)
                  : 1.2
              }
              renderLoader={(percentages: number) => (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {t("docs.loading")} {Math.round(percentages)}%
                </div>
              )}
              renderError={(error: LoadError) => (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {t("docs.errorLoading")}
                </div>
              )}
            />
          </Worker>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
